import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Heart, MessageCircle, Send, ImagePlus } from "lucide-react";

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState("");
  const [commentBox, setCommentBox] = useState({});

  const load = () => axios.get(`${API}/posts`).then(r => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await axios.post(`${API}/posts`, { content: text, photo: photo || null });
    setText(""); setPhoto("");
    load();
  };

  const like = async (id) => {
    await axios.post(`${API}/posts/${id}/like`);
    load();
  };

  const comment = async (id) => {
    const t = (commentBox[id] || "").trim();
    if (!t) return;
    await axios.post(`${API}/posts/${id}/comment`, { text: t });
    setCommentBox(prev => ({...prev, [id]: ""}));
    load();
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="feed-page">
      <h1 className="font-heading text-3xl font-extrabold">Pet Community</h1>

      <form onSubmit={post} className="bg-surface border border-line rounded-3xl p-4 shadow-soft" data-testid="post-form">
        <div className="flex items-start gap-3">
          {user?.picture ? (
            <img src={user.picture} className="w-10 h-10 rounded-full object-cover" alt="" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-leaf grid place-items-center font-bold">{user?.name?.[0]}</div>
          )}
          <div className="flex-1">
            <textarea
              data-testid="post-content"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Share a moment with your pet…"
              rows={2}
              className="w-full bg-surface-muted border border-line rounded-2xl px-4 py-3 outline-none focus:border-brand resize-none font-body"
            />
            {photo && <img src={photo} className="mt-2 rounded-2xl max-h-60 object-cover w-full" alt=""/>}
            <div className="flex items-center justify-between mt-2">
              <button type="button" onClick={() => {
                const u = prompt("Paste an image URL (Unsplash, etc.) or leave blank");
                if (u !== null) setPhoto(u);
              }} className="text-sm text-ink-soft font-bold inline-flex items-center gap-1.5 hover:text-brand">
                <ImagePlus className="w-4 h-4" /> Photo
              </button>
              <button data-testid="post-submit" type="submit" className="bg-brand text-white rounded-full px-5 py-2 text-sm font-bold hover:bg-brand-hover">Post</button>
            </div>
          </div>
        </div>
      </form>

      <div className="space-y-5">
        {posts.map(p => (
          <article key={p.post_id} data-testid={`post-${p.post_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft">
            <header className="p-4 flex items-center gap-3">
              {p.author_picture ? <img src={p.author_picture} className="w-10 h-10 rounded-full object-cover" alt=""/> : <div className="w-10 h-10 rounded-full bg-sky grid place-items-center text-white font-bold">{p.author?.[0]}</div>}
              <div>
                <div className="font-bold">{p.author}{p.pet_name && <span className="text-ink-soft font-normal"> · with {p.pet_name}</span>}</div>
                <div className="text-xs text-ink-soft">{new Date(p.created_at).toLocaleString()}</div>
              </div>
            </header>
            <div className="px-4 pb-3 text-[15px]">{p.content}</div>
            {p.photo && <img src={p.photo} alt="" className="w-full max-h-[480px] object-cover" />}
            <div className="px-4 py-3 flex items-center gap-4 border-t border-line">
              <button data-testid={`like-${p.post_id}`} onClick={() => like(p.post_id)} className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-soft hover:text-brand">
                <Heart className={`w-4 h-4 ${p.liked_by?.includes(user?.user_id) ? "fill-brand text-brand" : ""}`} /> {p.likes}
              </button>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-soft">
                <MessageCircle className="w-4 h-4" /> {p.comments?.length || 0}
              </span>
            </div>
            <div className="border-t border-line px-4 py-3 space-y-2 bg-surface-muted/30">
              {p.comments?.map(c => (
                <div key={c.comment_id} className="text-sm"><span className="font-bold">{c.author}</span>: <span className="text-ink-soft">{c.text}</span></div>
              ))}
              <div className="flex gap-2 pt-1">
                <input
                  data-testid={`comment-input-${p.post_id}`}
                  value={commentBox[p.post_id] || ""}
                  onChange={e => setCommentBox(prev => ({...prev, [p.post_id]: e.target.value}))}
                  onKeyDown={e => { if (e.key === "Enter") comment(p.post_id); }}
                  placeholder="Add a kind comment…"
                  className="flex-1 bg-surface border border-line rounded-full px-4 py-2 text-sm outline-none focus:border-brand"
                />
                <button data-testid={`comment-submit-${p.post_id}`} onClick={() => comment(p.post_id)} className="bg-ink text-white rounded-full w-9 h-9 grid place-items-center"><Send className="w-4 h-4"/></button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
